/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */

/**
 * Indirizzo del controller di export con i filtri nella query: i valori
 * vuoti (null, undefined, stringa vuota, false) non vengono inviati.
 */
export const exportQuery = (
  path: string,
  params: Record<string, string | number | boolean | null | undefined>,
): string => {
  const query = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value === null || value === undefined || value === '') return;
    if (value === false) return;
    query.set(key, String(value));
  });
  const text = query.toString();
  return text === '' ? path : `${path}?${text}`;
};

/** Nome del file da Content-Disposition (filename* o filename), o il predefinito. */
export const filenameFromDisposition = (
  header: string | null | undefined,
  fallback: string,
): string => {
  if (!header) return fallback;
  const star = /filename\*\s*=\s*(?:[\w-]+)?'[^']*'([^;]+)/i.exec(header);
  if (star) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch (e) {
      // codifica errata: si prova con filename
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
  return plain && plain[1].trim() !== '' ? plain[1].trim() : fallback;
};

/** Messaggio del corpo d'errore JSON `{error: {message}}` del controller. */
export const exportErrorMessage = (body: string, fallback: string): string => {
  try {
    const message = JSON.parse(body)?.error?.message;
    return typeof message === 'string' && message !== '' ? message : fallback;
  } catch (e) {
    return fallback;
  }
};

/**
 * Scarica un export. Prima una fetch con la sessione del browser: se la
 * risposta non è OK (422/403 con corpo JSON) lancia un Error col messaggio,
 * se è stata reindirizzata (login) o è HTML lancia l'errore di sessione,
 * altrimenti salva il blob come file tramite un object URL.
 */
export const downloadExport = async (
  url: string,
  fallbackName: string,
  fallbackError: string,
  sessionError: string = fallbackError,
): Promise<void> => {
  let response: Response;
  try {
    response = await fetch(url, {credentials: 'same-origin'});
  } catch (e) {
    // rete assente: il messaggio del browser non è per l'utente
    throw new Error(fallbackError);
  }
  if (!response.ok) {
    throw new Error(
      exportErrorMessage(await response.text().catch(() => ''), fallbackError),
    );
  }
  // Sessione scaduta: fetch segue il 302 e riceve la pagina di login (200 HTML)
  const contentType = response.headers.get('Content-Type') ?? '';
  if (response.redirected || /text\/html/i.test(contentType)) {
    throw new Error(sessionError);
  }
  const blob = await response.blob();
  const name = filenameFromDisposition(
    response.headers.get('Content-Disposition'),
    fallbackName,
  );
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
};
